/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState } from 'react';
import { Button, InputField } from '../../../components/uielements';
import {
  Heading,
  Main,
  SectionLeft,
  SignInSectionRight,
  SignUpSectionRight,
  AltText,
  LinkText,
  FormWrapper,
  InputLabel,
  HelperText,
  SectionLeftSignUp,
  Modal,
  ModalContent,
  StyledButton,
  ReestPasswordText,
  IconButton,
  PhoneInputContainer,
  CountryCodeSelect,
  SelectWrapper,
  IconWrapper,
  ModalHeading,
} from './login.style';
import { validateEmail, validatePassword } from '../../../helper/validationHelper';
import { useAuth } from '../../../hooks/useAuth';
import {
  getResetPasswordConfirmationCode,
  getVerificationEmailCode,
  registration,
  registrationVerification,
  RestPassword,
} from '../../../services/auth';
import { SuccessIcon } from '../../../icons';

import backgroundImgSignIn from '../../../assets/PNG/signin.png';
import backgroundImgSignUp from '../../../assets/PNG/signup.png';
import backgroundImgSignInCompress from '../../../assets/PNG/signin-compress.png';
import backgroundImgSignUpCompress from '../../../assets/PNG/signup-compress.png';
import useImageLoader from '../../../hooks/useImageLoader';
import { ChevronDown, LucideEye, LucideEyeOff } from 'lucide-react';
import { countryCodes } from '../../../settings/constants';
import TwoFactorModal from './modal/TwoFactorModal';

const AuthPage = () => {
  const [formState, setFormState] = useState('sign-in');

  const handleSignIn = () => {
    setFormState('sign-in');
  };

  const handleSignUp = () => {
    setFormState('sign-up');
  };

  const handleResetPassword = () => {
    setFormState('password-reset');
  };

  return (
    <Main role="main">
      {formState === 'sign-in' ? (
        <SignIn handleSignUp={handleSignUp} handleResetPassword={handleResetPassword} />
      ) : formState === 'password-reset' ? (
        <PasswordReset handleSignIn={handleSignIn} />
      ) : (
        <SignUp handleSignIn={handleSignIn} />
      )}
    </Main>
  );
};

export default AuthPage;

const SignIn = ({ handleSignUp, handleResetPassword }) => {
  const { login } = useAuth();
  const [errorMessages, setErrorMessages] = useState({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [verificationPage, setVerificationPage] = useState(false);
  const [email, setEmail] = useState('');

  const signInBg = useImageLoader(backgroundImgSignInCompress, backgroundImgSignIn);

  const togglePasswordVisibility = (e) => {
    e.preventDefault();
    setShowPassword((prevState) => !prevState);
  };

  const handleLogIn = async (event) => {
    try {
      setIsLoading(true);
      event.preventDefault();
      const data = new FormData(event.currentTarget);
      const isEmailValid = validateEmail(data.get('email'));
      const isPasswordValid = validatePassword(data.get('password'));

      const errors = {};

      if (!isEmailValid) {
        errors.email = 'Please enter a valid email address.';
      }
      if (!isPasswordValid) {
        errors.password = 'Please enter your password.';
      }

      if (Object.keys(errors).length > 0) {
        setErrorMessages(errors);
        setIsLoading(false);
        return;
      }
      setEmail(data.get('email'));

      await login(data.get('email'), data.get('password'));
    } catch (error) {
      setIsLoading(false);

      if (error.response && error.response.data.message === 'User Not Confirmed!') {
        alert('Please verify your email address to continue.');
        setVerificationPage(true);
      } else {
        alert('Credentials not found. Please check your email and password.');
      }
    }
  };

  const handleSignIn = () => {
    setVerificationPage(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setErrorMessages((prev) => ({
      ...prev,
      [name]: '',
    }));

    if (name === 'email' && value && !validateEmail(value)) {
      setErrorMessages((prev) => ({
        ...prev,
        email: 'Please enter a valid email address.',
      }));
    }
    if (name === 'password' && value && !validatePassword(value)) {
      setErrorMessages((prev) => ({
        ...prev,
        password: 'Password must be at least 6 characters.',
      }));
    }
  };

  return (
    <Main>
      {verificationPage ? (
        <EmailVerification email={email} onVerificationSuccess={handleSignIn} />
      ) : (
        <SectionLeft onSubmit={handleLogIn} role="form" aria-labelledby="signin-heading">
          <Heading id="signin-heading">Account Log in</Heading>

          <div id="alert-container" role="alert" aria-live="polite"></div>

          <FormWrapper>
            <InputLabel htmlFor="signin-email">Email</InputLabel>
            <InputField
              type="email"
              id="signin-email"
              placeholder="Email"
              name="email"
              onChange={handleInputChange}
              aria-required="true"
              aria-invalid={!!errorMessages.email}
              aria-describedby={errorMessages.email ? 'email-error' : undefined}
            />
            {errorMessages.email && (
              <HelperText id="email-error" role="alert">
                {errorMessages.email}
              </HelperText>
            )}
          </FormWrapper>
          <FormWrapper style={{ marginBottom: '8px' }}>
            <InputLabel htmlFor="signin-password">Password</InputLabel>
            <InputField
              type={showPassword ? 'text' : 'password'}
              id="signin-password"
              placeholder="Password"
              name="password"
              onChange={handleInputChange}
              aria-required="true"
              aria-invalid={!!errorMessages.password}
              aria-describedby={errorMessages.password ? 'password-error' : undefined}
            />

            <IconButton
              onClick={(e) => togglePasswordVisibility(e)}
              role="button"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  togglePasswordVisibility(e);
                }
              }}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <LucideEye style={{ width: '19px', height: '19px', color: '#646465' }} />
              ) : (
                <LucideEyeOff style={{ width: '19px', height: '19px', color: '#646465' }} />
              )}
            </IconButton>

            {errorMessages.password && (
              <HelperText id="password-error" role="alert">
                {errorMessages.password}
              </HelperText>
            )}
          </FormWrapper>

          <ReestPasswordText
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleResetPassword();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Switch to password reset form"
            onClick={handleResetPassword}
          >
            Forgot your password?
          </ReestPasswordText>

          <FormWrapper
            style={{
              display: 'flow',
              alignItems: 'center',
            }}
          >
            <label
              htmlFor="acknowledgment"
              style={{ fontSize: '12px' }}
              className="acknowledgment-text"
            >
              Urban Compass technologies acknowledges the &apos;Jinibara&apos;, &apos;Kabi
              Kabi&apos;, and &apos;Turrbal&apos; peoples as the Traditional Custodians of Moreton
              Bay and recognises their continuing connection to land, sea, culture, and community.
              We pay our respects to their Elders, past and present.
            </label>
          </FormWrapper>

          <StyledButton type="submit" loading={isLoading} aria-busy={isLoading}>
            {isLoading ? 'Logging in' : 'Log In'}
          </StyledButton>

          <AltText>
            Don&apos;t have an account?{' '}
            <LinkText
              onClick={handleSignUp}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleSignUp();
                }
              }}
              role="button"
              tabIndex={0}
              aria-label="Switch to sign up form"
            >
              Sign Up
            </LinkText>
          </AltText>
        </SectionLeft>
      )}
      <SignInSectionRight aria-hidden="true" bg={signInBg} />{' '}
      <TwoFactorModal
        isOpen={false}
        // onClose={() => setIsModalOpen(false)}
        onVerify={(code) => console.log('Verify:', code)}
        onResend={() => console.log('Resend code')}
        phoneNumber="+31 6 76953549"
        isLoading={false}
        error=""
        resendCooldown={0}
      />
    </Main>
  );
};

const EmailVerification = ({ email, onVerificationSuccess }) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const handleVerification = async () => {
    try {
      setIsLoading(true);
      await registrationVerification({ email, verificationCode });
      setModalOpen(true);
      setTimeout(() => {
        setModalOpen(false);
        onVerificationSuccess();
      }, 3000);
    } catch (error) {
      setIsLoading(false);
      alert('Verification failed. Please check your code.');
    }
  };

  const handleResendCode = async () => {
    try {
      setIsResending(true);
      await getVerificationEmailCode(email);
      alert('Verification code sent successfully! Please check your email.');
    } catch (error) {
      alert(`Failed to send verification code. Please try again.`);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <>
      <SectionLeftSignUp role="form" aria-labelledby="verification-heading">
        <Heading id="verification-heading">Email Verification</Heading>

        <div id="verification-alert" role="alert" aria-live="polite"></div>

        <FormWrapper>
          <InputLabel htmlFor="verification-email">Email</InputLabel>
          <InputField
            type="email"
            id="verification-email"
            value={email}
            disabled
            aria-label="Your registered email"
          />
        </FormWrapper>

        <FormWrapper>
          <InputLabel htmlFor="verification-code">Verification Code</InputLabel>
          <InputField
            type="text"
            id="verification-code"
            placeholder="Enter verification code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            aria-required="true"
            aria-label="Enter the verification code sent to your email"
          />
        </FormWrapper>

        <AltText>
          <LinkText
            onClick={handleResendCode}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleResendCode();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Resend verification code"
            style={{
              opacity: isResending ? 0.6 : 1,
              pointerEvents: isResending ? 'none' : 'auto',
            }}
          >
            {isResending ? 'Sending...' : 'Click Resend Code to get a new one.'}
          </LinkText>
        </AltText>

        <Button
          onClick={handleVerification}
          loading={isLoading}
          aria-busy={isLoading}
          disabled={isLoading}
        >
          Verify
        </Button>
      </SectionLeftSignUp>

      <Modal
        open={modalOpen}
        role="dialog"
        aria-modal="true"
        aria-labelledby="verification-success-modal-title"
      >
        <ModalContent>
          <SuccessIcon aria-hidden="true" />
          <ModalHeading id="verification-success-modal-title">
            Email Verification Done Successfully!
            <span role="img" aria-label="tada">
              🎉
            </span>
          </ModalHeading>
        </ModalContent>
      </Modal>
    </>
  );
};

const SignUp = ({ handleSignIn }) => {
  const [errorMessages, setErrorMessages] = useState({
    email: '',
    password: '',
    userName: '',
    address: '',
    phone: '',
  });
  const [verificationPage, setVerificationPage] = useState(false);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [countryCode, setCountryCode] = useState('+61'); // Default to Australia

  const signUnBg = useImageLoader(backgroundImgSignUpCompress, backgroundImgSignUp);

  const togglePasswordVisibility = (e) => {
    e.preventDefault();
    setShowPassword((prevState) => !prevState);
  };

  const handleRegistration = async (event) => {
    try {
      setIsLoading(true);
      event.preventDefault();
      const data = new FormData(event.currentTarget);

      const errors = {};

      if (!data.get('userName')) {
        errors.userName = 'Name is required.';
      }
      if (!data.get('address')) {
        errors.address = 'Address is required.';
      }

      // Get the phone number without country code from the form
      const phoneNumber = data.get('phone');
      if (!phoneNumber) {
        errors.phone = 'Phone number is required.';
      }

      if (!validateEmail(data.get('email'))) {
        errors.email = 'Please enter a valid email address.';
      }
      if (!validatePassword(data.get('password'))) {
        errors.password = 'Please enter your password.';
      }

      if (Object.keys(errors).length > 0) {
        setErrorMessages(errors);
        setIsLoading(false);
        return;
      }

      // Combine country code with phone number for the payload
      const fullPhoneNumber = `${countryCode}${phoneNumber}`;

      const payload = {
        username: data.get('userName'),
        email: data.get('email'),
        phone: +fullPhoneNumber, // Use the combined phone number with country code
        password: data.get('password'),
        address: data.get('address'),
      };

      await registration(payload);
      setEmail(payload.email);
      setVerificationPage(true);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      alert('Registration failed. Please try again.');
    }
  };

  const handleVerificationSuccess = () => {
    handleSignIn();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setErrorMessages((prev) => ({ ...prev, [name]: '' }));

    if (name === 'email' && value && !validateEmail(value)) {
      setErrorMessages((prev) => ({
        ...prev,
        email: 'Please enter a valid email address.',
      }));
    }
    if (name === 'password' && value && !validatePassword(value)) {
      setErrorMessages((prev) => ({
        ...prev,
        password: 'Password must be at least 6 characters.',
      }));
    }
  };

  const handleCountryCodeChange = (e) => {
    setCountryCode(e.target.value);
  };

  return (
    <Main>
      {verificationPage ? (
        <EmailVerification email={email} onVerificationSuccess={handleVerificationSuccess} />
      ) : (
        <SectionLeftSignUp
          onSubmit={handleRegistration}
          role="form"
          aria-labelledby="signup-heading"
        >
          <Heading id="signup-heading">Account Sign up</Heading>

          <div id="signup-alert" role="alert" aria-live="polite"></div>

          <FormWrapper>
            <InputLabel htmlFor="signup-userName">Name</InputLabel>
            <InputField
              type="text"
              id="signup-userName"
              placeholder="Enter your name"
              name="userName"
              onChange={handleInputChange}
              aria-required="true"
              aria-invalid={!!errorMessages.userName}
              aria-describedby={errorMessages.userName ? 'userName-error' : undefined}
            />
            {errorMessages.userName && (
              <HelperText id="userName-error" role="alert">
                {errorMessages.userName}
              </HelperText>
            )}
          </FormWrapper>

          <FormWrapper>
            <InputLabel htmlFor="signup-address">Address</InputLabel>
            <InputField
              type="text"
              id="signup-address"
              placeholder="Enter your address"
              name="address"
              onChange={handleInputChange}
              aria-required="true"
              aria-invalid={!!errorMessages.address}
              aria-describedby={errorMessages.address ? 'address-error' : undefined}
            />
            {errorMessages.address && (
              <HelperText id="address-error" role="alert">
                {errorMessages.address}
              </HelperText>
            )}
          </FormWrapper>

          <FormWrapper>
            <InputLabel htmlFor="signup-phone">Phone</InputLabel>
            <PhoneInputContainer>
              <SelectWrapper>
                <CountryCodeSelect
                  id="country-code"
                  value={countryCode}
                  onChange={handleCountryCodeChange}
                  aria-label="Select country code"
                >
                  {countryCodes.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.code} {country.country}
                    </option>
                  ))}
                </CountryCodeSelect>{' '}
                <IconWrapper>
                  <ChevronDown size={16} color="#606266" />
                </IconWrapper>
              </SelectWrapper>
              <InputField
                type="tel"
                id="signup-phone"
                placeholder="Enter your phone number"
                name="phone"
                onChange={handleInputChange}
                aria-required="true"
                aria-invalid={!!errorMessages.phone}
                aria-describedby={errorMessages.phone ? 'phone-error' : undefined}
              />
            </PhoneInputContainer>
            {errorMessages.phone && (
              <HelperText id="phone-error" role="alert">
                {errorMessages.phone}
              </HelperText>
            )}
          </FormWrapper>

          <FormWrapper>
            <InputLabel htmlFor="signup-email">Email</InputLabel>
            <InputField
              type="email"
              id="signup-email"
              placeholder="Enter your email"
              name="email"
              onChange={handleInputChange}
              aria-required="true"
              aria-invalid={!!errorMessages.email}
              aria-describedby={errorMessages.email ? 'email-error' : undefined}
            />
            {errorMessages.email && (
              <HelperText id="email-error" role="alert">
                {errorMessages.email}
              </HelperText>
            )}
          </FormWrapper>

          <FormWrapper>
            <InputLabel htmlFor="signup-password">Password</InputLabel>
            <InputField
              type={showPassword ? 'text' : 'password'}
              id="signup-password"
              placeholder="Create password"
              name="password"
              onChange={handleInputChange}
              aria-required="true"
              aria-invalid={!!errorMessages.password}
              aria-describedby={errorMessages.password ? 'password-error' : undefined}
            />
            <IconButton
              onClick={(e) => togglePasswordVisibility(e)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  togglePasswordVisibility(e);
                }
              }}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <LucideEye style={{ width: '19px', height: '19px', color: '#646465' }} />
              ) : (
                <LucideEyeOff style={{ width: '19px', height: '19px', color: '#646465' }} />
              )}
            </IconButton>
            {errorMessages.password && (
              <HelperText id="password-error" role="alert">
                {errorMessages.password}
              </HelperText>
            )}
          </FormWrapper>

          <Button type="submit" aria-busy={isLoading} loading={isLoading}>
            {isLoading ? 'Signing up' : 'Sign Up'}
          </Button>

          <AltText>
            Already have an account?{' '}
            <LinkText
              onClick={handleSignIn}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleSignIn();
                }
              }}
              role="button"
              tabIndex={0}
              aria-label="Switch to sign in form"
            >
              Sign In
            </LinkText>
          </AltText>
        </SectionLeftSignUp>
      )}

      <SignUpSectionRight aria-hidden="true" bg={signUnBg} />
    </Main>
  );
};

const PasswordReset = ({ handleSignIn }) => {
  const [errorMessages, setErrorMessages] = useState({
    email: '',
    password: '',
    confirmationCode: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [getConfirmationCode, setGetConfirmationCode] = useState(false);

  const signInBg = useImageLoader(backgroundImgSignInCompress, backgroundImgSignIn);

  const togglePasswordVisibility = (e) => {
    e.preventDefault();
    setShowPassword((prevState) => !prevState);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const data = new FormData(e.currentTarget);
    const email = data.get('email');
    const password = data.get('password');
    const confirmationCode = data.get('confirmationCode');

    // Validate email
    if (!validateEmail(email)) {
      setErrorMessages((prev) => ({
        ...prev,
        email: 'Please enter a valid email address.',
      }));
      setIsLoading(false);
      return;
    }

    try {
      if (!getConfirmationCode) {
        const code = await getResetPasswordConfirmationCode(email);
        if (code) {
          setGetConfirmationCode(true);
        }
      } else {
        // Validate password
        if (!validatePassword(password)) {
          setErrorMessages((prev) => ({
            ...prev,
            password: 'Password must be at least 6 characters.',
          }));
          setIsLoading(false);
          return;
        }
        if (!confirmationCode) {
          setErrorMessages((prev) => ({
            ...prev,
            confirmationCode: 'Please enter the confirmation code.',
          }));
          setIsLoading(false);
          return;
        }

        await RestPassword(email, password, confirmationCode);
        alert('Password reset successful.');
        setGetConfirmationCode(false);
        handleSignIn();
      }
    } catch (error) {
      alert('Oops, an error occurred. Please try again later.');
      setGetConfirmationCode(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setErrorMessages((prev) => ({
      ...prev,
      [name]: '',
    }));

    if (name === 'email' && value && !validateEmail(value)) {
      setErrorMessages((prev) => ({
        ...prev,
        email: 'Please enter a valid email address.',
      }));
    }

    if (name === 'password' && value && !validatePassword(value)) {
      setErrorMessages((prev) => ({
        ...prev,
        password: 'Password must be at least 6 characters.',
      }));
    }
  };

  return (
    <Main>
      <SectionLeft
        onSubmit={handleResetPassword}
        role="form"
        aria-labelledby="password-reset-heading"
      >
        <Heading id="password-reset-heading">Forgot Password</Heading>

        <div id="alert-container" role="alert" aria-live="polite"></div>

        <FormWrapper>
          <InputLabel htmlFor="email">Email Address</InputLabel>
          <InputField
            type="email"
            id="email"
            name="email"
            placeholder="Enter your email"
            onChange={handleInputChange}
            aria-required="true"
            aria-invalid={!!errorMessages.email}
            aria-describedby={errorMessages.email ? 'email-error' : undefined}
            autoComplete="email"
          />
          {errorMessages.email && (
            <HelperText id="email-error" role="alert">
              {errorMessages.email}
            </HelperText>
          )}
        </FormWrapper>

        {getConfirmationCode && (
          <>
            <FormWrapper>
              <InputLabel htmlFor="new-password">New Password</InputLabel>
              <InputField
                type={showPassword ? 'text' : 'password'}
                id="new-password"
                name="password"
                placeholder="Enter your new password"
                onChange={handleInputChange}
                aria-required="true"
                aria-invalid={!!errorMessages.password}
                aria-describedby={errorMessages.password ? 'password-error' : undefined}
                autoComplete="new-password"
              />
              <IconButton
                onClick={(e) => togglePasswordVisibility(e)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    togglePasswordVisibility(e);
                  }
                }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <LucideEye style={{ width: '19px', height: '19px', color: '#646465' }} />
                ) : (
                  <LucideEyeOff style={{ width: '19px', height: '19px', color: '#646465' }} />
                )}
              </IconButton>
              {errorMessages.password && (
                <HelperText id="password-error" role="alert">
                  {errorMessages.password}
                </HelperText>
              )}
            </FormWrapper>

            <FormWrapper>
              <InputLabel htmlFor="confirmation-code">Confirmation Code</InputLabel>
              <InputField
                type="text"
                id="confirmation-code"
                name="confirmationCode"
                placeholder="Enter confirmation code"
                onChange={handleInputChange}
                aria-required="true"
                aria-invalid={!!errorMessages.confirmationCode}
                aria-describedby={
                  errorMessages.confirmationCode ? 'confirmation-code-error' : undefined
                }
              />
              {errorMessages.confirmationCode && (
                <HelperText id="confirmation-code-error" role="alert">
                  {errorMessages.confirmationCode}
                </HelperText>
              )}
            </FormWrapper>
          </>
        )}

        <StyledButton type="submit" loading={isLoading} aria-busy={isLoading}>
          {getConfirmationCode ? 'Reset Password' : 'Get Code'}
        </StyledButton>

        <ReestPasswordText
          role="link"
          tabIndex={0}
          onClick={handleSignIn}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ' ? handleSignIn() : null)}
          aria-label="Return to the Sign In page"
        >
          Take me back to the Sign In page, Click Here.
        </ReestPasswordText>
      </SectionLeft>

      <SignInSectionRight aria-hidden="true" bg={signInBg} />
    </Main>
  );
};
